import React, { FunctionComponent, useCallback, useMemo } from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import {
  CollectionActions,
  CollectionModifyOrdersArgs,
  CollectionType,
  DefaultCollateralIdentifierType,
  EntityType,
  EntityTypeEnum,
  HaircutType,
  NegotiationFieldMeta,
  OrderDetail,
  OrderDetailSummary,
  OrderEvent,
  OrderTerm,
  PlatformFeature,
  SubscriptionIdentifier,
  TradingEntityMeta
} from 'api'
import { GridApi } from 'ag-grid-community'
import { RepositoryActions } from 'store/repository'
import { DataGrid, filteredColumnDefsBuilder, GridId } from 'view/components/datagrid'
import { getStagingGridColumnDefs, stagingGridOptions } from './StagingOrdersGridModel'
import { StagingGridPlaceholder } from './StagingGridPlaceholder'
import { OrganisationFilterSwitch } from 'view/components/datagrid/toolbar/OrganisationFilterSwitch'
import { StagingGroupByToggles } from '../StagingGroupByToggles'
import { OrderFieldNames, StagingGridColumnId } from './StagingGridColumnNames'
import { CollectionColumnId, ContainerCollectionColumnId } from 'view/routes/ColumnNames'
import { createEntitySubscriptionSelector, preparedStagingOrderIdsSelector } from 'selectors/selectionSelectors'
import { canUploadPositionsSelector } from 'selectors/permissionSelectors'
import { createStagingOrderDetailsSelector } from 'selectors/orderSelectors'
import {
  createStagingOrdersCollectionContainerGroupRowData,
  createStagingOrdersCollectionGroupRowDataSelector,
  StagingGroupContainerCollectionRowData,
  StagingGroupRowData
} from 'selectors/orderSummarySelectors'
import { Nullable, primitive, sliceFieldName } from 'utils'
import { createEntitySnapshotLoadedSelector } from 'selectors/systemSelectors'
import { stagingRowDataGetter } from 'view/shared/datagrid'
import { isOrderDetail, isOrderSummary, isSmartGroupOrderDetailSummary } from 'view/routes/requests/shared/utils'
import { createPlatformFeatureEnabledSelector } from 'selectors/platformSelectors'
import { defaultCollateralIdentifierTypeSelector } from 'selectors/userPreferenceSelectors'
import { startConsiderationOverrideEnabledSelector, isStockBorrowingLendingEnabledSelector } from 'selectors/featureSelectors'
import { BulkDateUpdateInput } from '../BulkDateUpdate/BulkDateUpdateInput'
import { tradingEntityMetaByAliasGetterSelector } from 'selectors/tradingEntitySelectors'
import { isNil } from 'lodash'
import { useSelector } from 'react-redux'
import { createEntityGetterSelector } from 'selectors/entitySelectors'
import moment from 'moment'
import { generateExportFileName } from 'view/components/datagrid/gridUtils/utils'
import { SubscriptionDetail } from 'store/selection/selectionTypes'
import { hasCurrentOrganisationLockFixEnabledSelector } from 'selectors/organisationSelectors'
import { isAdminGatewayUserSelector } from 'selectors/userSelectors'

export interface StagingOrdersGridProps {
  getMetaByAlias: (alias: string, ownerTradingEntityId: string) => TradingEntityMeta | null
  orderList: OrderDetail[]
  isLockFixOrganisation: boolean
  collectionGroupRowData: StagingGroupRowData | null
  containerCollectionGroupRowData: StagingGroupContainerCollectionRowData | null
  preparedOrderIds: string[]
  evergreenNoticeEnabled: boolean
  canUploadPositions: boolean
  onUpdateField: (entityType: EntityType, entityId: string, fieldName: string, fieldValue: Nullable<primitive>) => unknown
  snapshotLoaded: boolean
  onModifyCollectionOrders: (args: CollectionModifyOrdersArgs) => unknown
  allowPartialEnabled: boolean
  defaultCollateralIdentifierType: DefaultCollateralIdentifierType
  startConsiderationOverrideEnabled: boolean
  stockBorrowingLendingEnabled: boolean
  subscriptionDetail: SubscriptionDetail | undefined
  isAdmin: boolean
}

const mapStateToProps = createStructuredSelector({
  orderList: createStagingOrderDetailsSelector(),
  isLockFixOrganisation: hasCurrentOrganisationLockFixEnabledSelector,
  collectionGroupRowData: createStagingOrdersCollectionGroupRowDataSelector(),
  containerCollectionGroupRowData: createStagingOrdersCollectionContainerGroupRowData(),
  preparedOrderIds: preparedStagingOrderIdsSelector,
  canUploadPositions: canUploadPositionsSelector,
  snapshotLoaded: createEntitySnapshotLoadedSelector(EntityTypeEnum.ORDER),
  allowPartialEnabled: createPlatformFeatureEnabledSelector(PlatformFeature.ALLOW_PARTIAL),
  evergreenNoticeEnabled: createPlatformFeatureEnabledSelector(PlatformFeature.ENABLE_EVERGREENS),
  defaultCollateralIdentifierType: defaultCollateralIdentifierTypeSelector,
  startConsiderationOverrideEnabled: startConsiderationOverrideEnabledSelector,
  stockBorrowingLendingEnabled: isStockBorrowingLendingEnabledSelector,
  getMetaByAlias: tradingEntityMetaByAliasGetterSelector,
  subscriptionDetail: createEntitySubscriptionSelector(
    () => EntityTypeEnum.ORDER,
    () => SubscriptionIdentifier.STAGING
  ),
  isAdmin: isAdminGatewayUserSelector
})

const mapDispatchToProps = {
  onUpdateField: RepositoryActions.updateField,
  onModifyCollectionOrders: CollectionActions.modifyOrders
}

const collateralBasketSelector = createEntityGetterSelector(EntityType.COLLATERAL_BASKET)

export const StagingOrdersGridComponent: FunctionComponent<StagingOrdersGridProps> = ({
  orderList,
  isLockFixOrganisation,
  canUploadPositions,
  evergreenNoticeEnabled,
  collectionGroupRowData,
  containerCollectionGroupRowData,
  snapshotLoaded,
  allowPartialEnabled,
  preparedOrderIds,
  onUpdateField,
  onModifyCollectionOrders,
  defaultCollateralIdentifierType,
  startConsiderationOverrideEnabled,
  stockBorrowingLendingEnabled,
  getMetaByAlias,
  subscriptionDetail,
  isAdmin
}) => {
  const getCollateralBasket = useSelector(collateralBasketSelector)
  const isBasket = (id: string) => !!getCollateralBasket(id)

  const groupRowData = useMemo(
    () =>
      collectionGroupRowData
        ? {
            [CollectionColumnId.COLLECTION_NAME]: collectionGroupRowData,
            ...(containerCollectionGroupRowData && {
              [ContainerCollectionColumnId.CONTAINER_COLLECTION_NAME]: containerCollectionGroupRowData
            })
          }
        : undefined,
    [collectionGroupRowData, containerCollectionGroupRowData]
  )

  const filteredColumnDefs = useMemo(
    () =>
      filteredColumnDefsBuilder(getStagingGridColumnDefs(isLockFixOrganisation))
        .withoutIf(!evergreenNoticeEnabled, [StagingGridColumnId.EVERGREEN_NOTICE_PERIOD])
        .withoutIf(!allowPartialEnabled, [
          StagingGridColumnId.ALLOW_PARTIAL,
          StagingGridColumnId.ORDER_TARGET_FILL_NOTIONAL,
          StagingGridColumnId.ORDER_TARGET_FILL_START_CONSIDERATION
        ])
        .withoutIf(!isAdmin, [StagingGridColumnId.MY_FICC_ENTITY_SHORT_NAME])
        .withoutIf(!startConsiderationOverrideEnabled, [StagingGridColumnId.START_CONSIDERATION_OVERRIDE])
        .withoutIf(!stockBorrowingLendingEnabled, [
          StagingGridColumnId.SECONDARY_COLLATERAL_NAME,
          StagingGridColumnId.ORDER_FEE,
          StagingGridColumnId.STOCK_BORROW_LENDING_TYPE
        ])
        .build(),
    [
      allowPartialEnabled,
      evergreenNoticeEnabled,
      isLockFixOrganisation,
      startConsiderationOverrideEnabled,
      stockBorrowingLendingEnabled,
      isAdmin
    ]
  )

  const canEditRow = useCallback(
    (orderDetail: OrderDetail | OrderDetailSummary) => {
      if (isOrderDetail(orderDetail)) {
        return !preparedOrderIds.includes(orderDetail.order.orderId) && orderDetail.availableEvents.includes(OrderEvent.MODIFY)
      }

      return true
    },
    [preparedOrderIds]
  )

  const handleCellValueUpdated = (orderId: string, orderDetail: OrderDetail, field: string, newValue: string) => {
    if (isSmartGroupOrderDetailSummary(orderDetail) && orderDetail.collection.id) {
      handleSmartGroupCollectionCellSummaryUpdate(orderDetail, field, newValue)
      return
    }

    if (orderDetail.collection?.id && orderDetail.collection.type === CollectionType.ALL_OR_NOTHING) {
      if (field === OrderFieldNames.RFQ_TYPE) {
        onModifyCollectionOrders({
          collectionId: orderDetail.collection?.id,
          updatedValues: {
            [NegotiationFieldMeta.RFQ_TYPE]: newValue
          }
        })
        return
      }

      if (field === OrderFieldNames.CALLABLE_TYPE && isOrderSummary(orderDetail.order)) {
        onModifyCollectionOrders({
          collectionId: orderDetail.collection?.id,
          updatedValues: {
            [NegotiationFieldMeta.CALLABLE_TYPE]: newValue
          }
        })
        return
      }

      if (field === OrderFieldNames.CALLABLE_PERIOD_DAYS && isOrderSummary(orderDetail.order)) {
        onModifyCollectionOrders({
          collectionId: orderDetail.collection?.id,
          updatedValues: {
            [NegotiationFieldMeta.CALLABLE_PERIOD_DAYS]: newValue
          }
        })
        return
      }

      if (field === OrderFieldNames.HAIRCUT && isOrderSummary(orderDetail.order)) {
        onModifyCollectionOrders({
          collectionId: orderDetail.collection?.id,
          updatedValues: {
            [NegotiationFieldMeta.HAIRCUT]: newValue,
            ...(newValue === null && {
              [NegotiationFieldMeta.HAIRCUT_TYPE]: null
            }),
            ...(newValue !== null &&
              orderDetail.order.haircutType === null && {
                [NegotiationFieldMeta.HAIRCUT_TYPE]: HaircutType.HAIRCUT
              })
          }
        })
        return
      }

      if (field === OrderFieldNames.HAIRCUT_TYPE && isOrderSummary(orderDetail.order)) {
        onModifyCollectionOrders({
          collectionId: orderDetail.collection?.id,
          updatedValues: {
            [NegotiationFieldMeta.HAIRCUT_TYPE]: newValue
          }
        })
        return
      }
    }

    if (field === OrderFieldNames.COUNTERPARTY_NAME || field === OrderFieldNames.ALIAS) {
      const counterpartyFieldName = sliceFieldName(OrderFieldNames.COUNTERPARTY_NAME, 1)
      const aliasFieldName = sliceFieldName(OrderFieldNames.ALIAS, 1)

      const orderDetailFundId = orderDetail.orderFund?.id
      const isAlias = orderDetailFundId ? !!getMetaByAlias(newValue, orderDetailFundId) : false

      if (isAlias) {
        onUpdateField(EntityType.ORDER, orderId, aliasFieldName, newValue)
      } else {
        onUpdateField(EntityType.ORDER, orderId, counterpartyFieldName, newValue)
      }

      return
    }

    if (field === OrderFieldNames.COLLATERAL_ID || field === OrderFieldNames.BASKET_ID) {
      onUpdateField(EntityType.ORDER, orderId, 'startConsiderationOverride', false)
    }

    const fieldName = sliceFieldName(field, 1)
    onUpdateField(EntityType.ORDER, orderId, fieldName, newValue)
  }

  const handleSmartGroupCollectionCellSummaryUpdate = (orderDetail: OrderDetailSummary, field: string, newValue: string) => {
    const collectionId = orderDetail.collection.id!

    switch (field) {
      case OrderFieldNames.START_DATE:
        onModifyCollectionOrders({
          collectionId,
          updatedValues: {
            [NegotiationFieldMeta.START_DATE]: newValue,
            [NegotiationFieldMeta.TERM]: isNil(orderDetail.order.endDate) ? OrderTerm.CUSTOM_O : OrderTerm.CUSTOM
          }
        })
        break
      case OrderFieldNames.END_DATE:
        onModifyCollectionOrders({
          collectionId,
          updatedValues: {
            [NegotiationFieldMeta.END_DATE]: newValue,
            [NegotiationFieldMeta.TERM]: OrderTerm.CUSTOM
          }
        })
        break
      case OrderFieldNames.TERM:
        onModifyCollectionOrders({
          collectionId,
          updatedValues: {
            [NegotiationFieldMeta.TERM]: newValue
          }
        })
        break
      case OrderFieldNames.ISIN:
      case OrderFieldNames.BASKET_ID: {
        if (isBasket(newValue)) {
          onModifyCollectionOrders({
            collectionId,
            updatedValues: {
              [NegotiationFieldMeta.BASKET]: newValue
            }
          })
        } else {
          onModifyCollectionOrders({
            collectionId,
            updatedValues: {
              [NegotiationFieldMeta.COLLATERAL]: newValue
            }
          })
        }
        break
      }
      case OrderFieldNames.EVERGREEN_NOTICE_PERIOD:
        onModifyCollectionOrders({
          collectionId,
          updatedValues: {
            [NegotiationFieldMeta.EVERGREEN_NOTICE_PERIOD]: newValue
          }
        })
        break
      case OrderFieldNames.DIRECTION:
        onModifyCollectionOrders({
          collectionId,
          updatedValues: {
            [NegotiationFieldMeta.DIRECTION]: newValue
          }
        })
        break
      default:
        throw new Error(`Unimplemented smart group summary cell update: ${field}`)
    }
  }

  const shouldRedrawOnCellValueUpdate = (field: string) =>
    [
      OrderFieldNames.ISIN,
      OrderFieldNames.BASKET_ID,
      OrderFieldNames.FUND_ID,
      OrderFieldNames.ALLOW_PARTIAL,
      OrderFieldNames.REPO_TYPE
    ].includes(field)

  const handleAfterRestoreDefaultLayout = (gridApi: GridApi) => {
    const hasCollections = orderList.some(orderDetail => orderDetail.collection)

    if (hasCollections) {
      gridApi.setColumnsVisible([CollectionColumnId.COLLECTION_NAME], true)
      gridApi.setRowGroupColumns([ContainerCollectionColumnId.CONTAINER_COLLECTION_NAME, CollectionColumnId.COLLECTION_NAME])
    }

    gridApi.setColumnsVisible(
      [StagingGridColumnId.COLLATERAL_ISIN_OR_BUI],
      defaultCollateralIdentifierType !== DefaultCollateralIdentifierType.CUSIP
    )

    gridApi.setColumnsVisible(
      [StagingGridColumnId.COLLATERAL_CUSIP],
      defaultCollateralIdentifierType !== DefaultCollateralIdentifierType.ISIN
    )
  }

  const groupSortComparator = useCallback((groupAItems: OrderDetail[], groupBItems: OrderDetail[]) => {
    const doBothCollectionsBelongToSameAONX =
      groupAItems.every(o => o.order.containerCollectionId) &&
      groupBItems.every(o => o.order.containerCollectionId) &&
      groupAItems[0].order.containerCollectionId === groupBItems[0].order.containerCollectionId

    if (doBothCollectionsBelongToSameAONX) {
      const groupAEndDate = moment.max(groupAItems.map(d => moment(d.order.endDate)))
      const groupBEndDate = moment.max(groupBItems.map(d => moment(d.order.endDate)))

      if (groupAEndDate.isBefore(groupBEndDate)) {
        return -1
      } else if (groupAEndDate.isAfter(groupBEndDate)) {
        return 1
      }
    }

    return 0
  }, [])

  return (
    <DataGrid
      gridId={GridId.StagingGrid}
      gridClass="upload-grid"
      gridOptions={stagingGridOptions}
      rowData={orderList}
      columnDefs={filteredColumnDefs}
      placeholder={canUploadPositions ? StagingGridPlaceholder : undefined}
      canEditRow={canEditRow}
      trackGridRowInEdit={true}
      defaultGroupSortComparator={groupSortComparator}
      customToolbarSwitches={[OrganisationFilterSwitch]}
      customToolbarComponents={[BulkDateUpdateInput]}
      GroupByToggles={StagingGroupByToggles}
      shouldRedrawOnCellValueUpdate={shouldRedrawOnCellValueUpdate}
      onCellValueUpdated={handleCellValueUpdated}
      groupRowData={groupRowData}
      showLoader={!snapshotLoaded}
      editableRowGroupColumnConfig={{
        [CollectionColumnId.COLLECTION_NAME]: stagingRowDataGetter
      }}
      onAfterRestoreDefaultLayout={handleAfterRestoreDefaultLayout}
      saveExpandedCollapsedRows={true}
      exportFileName={generateExportFileName('Staging')}
      subscriptionDetail={subscriptionDetail}
    />
  )
}

export const StagingOrdersGrid = connect(mapStateToProps, mapDispatchToProps)(StagingOrdersGridComponent)
